import { useCallback, useInsertionEffect, useRef } from 'react';

// Approximation of React's upcoming useEffectEvent hook
// This gives us a non-reactive effect event callback, it will always use the latest version of
// the callback, rather than the one that was closed over.
const useEffectEventShim: {
  // When TS can infer args/return from the callback itself:
  <A extends Array<any>, R>(fn: (...args: A) => R): (...args: A) => R;
  // When TS should *contextually* type the callback (e.g., prop position):
  <F extends (...args: Array<any>) => any>(fn: F): F;
} = (fn: (...args: Array<any>) => any) => {
  const ref = useRef(fn);

  useInsertionEffect(() => {
    ref.current = fn;
  }, [fn]);

  // Return exactly the same function *type* as given, so overloads/generics remain visible.
  return useCallback((...args: Parameters<typeof fn>) => {
    const f = ref.current;
    return f(...args);
  }, []);
};

export default useEffectEventShim;
